import type { OntologyEntry, Dimension } from './types.js';

interface AddResult {
  added: OntologyEntry[];
  skipped: OntologyEntry[];
  rejected: OntologyEntry[];
}

export class OntologyRegistry {
  private byDimensionAndSlug = new Map<string, OntologyEntry>();
  private byDimension = new Map<Dimension, OntologyEntry[]>();
  private childrenIndex = new Map<string, OntologyEntry[]>();

  constructor(entries: OntologyEntry[]) {
    for (const entry of entries) {
      this.indexEntry(entry);
    }
  }

  private compositeKey(dimension: Dimension, slug: string): string {
    return `${dimension}::${slug}`;
  }

  private indexEntry(entry: OntologyEntry): void {
    const key = this.compositeKey(entry.dimension, entry.slug);
    this.byDimensionAndSlug.set(key, entry);
    const dimList = this.byDimension.get(entry.dimension) ?? [];
    dimList.push(entry);
    this.byDimension.set(entry.dimension, dimList);
    if (entry.parentSlug) {
      const parentKey = this.compositeKey(entry.dimension, entry.parentSlug);
      const children = this.childrenIndex.get(parentKey) ?? [];
      children.push(entry);
      this.childrenIndex.set(parentKey, children);
    }
  }

  getBySlug(dimension: Dimension, slug: string): OntologyEntry | undefined {
    return this.byDimensionAndSlug.get(this.compositeKey(dimension, slug));
  }

  getDimension(dimension: Dimension): OntologyEntry[] {
    return this.byDimension.get(dimension) ?? [];
  }

  getRoots(dimension: Dimension): OntologyEntry[] {
    return this.getDimension(dimension).filter((e) => !e.parentSlug);
  }

  getChildren(dimension: Dimension, slug: string): OntologyEntry[] {
    return this.childrenIndex.get(this.compositeKey(dimension, slug)) ?? [];
  }

  getDescendants(dimension: Dimension, slug: string): OntologyEntry[] {
    const result: OntologyEntry[] = [];
    const stack = this.getChildren(dimension, slug);
    while (stack.length > 0) {
      const current = stack.pop()!;
      result.push(current);
      stack.push(...this.getChildren(dimension, current.slug));
    }
    return result;
  }

  getAncestors(dimension: Dimension, slug: string): OntologyEntry[] {
    const result: OntologyEntry[] = [];
    let current = this.getBySlug(dimension, slug);
    while (current?.parentSlug) {
      const parent = this.getBySlug(dimension, current.parentSlug);
      if (!parent) break;
      result.push(parent);
      current = parent;
    }
    return result;
  }

  isValidSlug(dimension: Dimension, slug: string): boolean {
    return this.byDimensionAndSlug.has(this.compositeKey(dimension, slug));
  }

  addEntries(entries: OntologyEntry[]): AddResult {
    const added: OntologyEntry[] = [];
    const skipped: OntologyEntry[] = [];
    const rejected: OntologyEntry[] = [];
    for (const entry of entries) {
      const key = this.compositeKey(entry.dimension, entry.slug);
      if (this.byDimensionAndSlug.has(key)) {
        skipped.push(entry);
        continue;
      }
      if (entry.parentSlug && !this.byDimensionAndSlug.has(this.compositeKey(entry.dimension, entry.parentSlug))) {
        rejected.push(entry);
        continue;
      }
      this.indexEntry(entry);
      added.push(entry);
    }
    return { added, skipped, rejected };
  }
}
